import React, { useRef, useState } from 'react';
import Icon from './Icon';

function CameraCapture({ onCapture, isProcessing, error, capturedImage }) {
  const fileInputRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const frontImageRef = useRef(null); // Store front image in ref for reliable access
  const [stream, setStream] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [isDoubleSided, setIsDoubleSided] = useState(false);
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [currentSide, setCurrentSide] = useState('front'); // 'front' or 'back'

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }, // Use back camera on mobile
      });
      setStream(mediaStream);
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error('Camera access error:', err);
      alert('Could not access camera. Please allow camera permissions or upload an image instead.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `business-card-${currentSide}.jpg`, { type: 'image/jpeg' });
        
        if (currentSide === 'front') {
          const capturedFront = file;
          setFrontImage(capturedFront);
          frontImageRef.current = capturedFront; // Store in ref
          if (isDoubleSided) {
            setCurrentSide('back');
            // Keep camera open for back side
          } else {
            onCapture(capturedFront);
            stopCamera();
            setFrontImage(null);
            frontImageRef.current = null;
          }
        } else {
          const capturedBack = file;
          setBackImage(capturedBack);
          // Use ref to get front image reliably
          const frontFile = frontImageRef.current;
          if (frontFile) {
            onCapture(frontFile, capturedBack);
            stopCamera();
            setFrontImage(null);
            setBackImage(null);
            frontImageRef.current = null;
            setCurrentSide('front');
          }
        }
      }
    }, 'image/jpeg', 0.9);
  };

  const handleFileSelect = (e, side = 'front') => {
    const file = e.target.files[0];
    if (!file) {
      e.target.value = '';
      return;
    }
    
    console.log('File selected for side:', side, 'File:', file.name);
    
    if (side === 'front') {
      console.log('Setting front image');
      setFrontImage(file);
      frontImageRef.current = file; // Store in ref for reliable access
      if (isDoubleSided) {
        // Wait for back image
        setCurrentSide('back');
      } else {
        onCapture(file);
        setFrontImage(null);
        frontImageRef.current = null;
      }
    } else if (side === 'back') {
      // Back side upload - ONLY set back image, don't touch front
      console.log('Setting back image, front image ref:', frontImageRef.current);
      const backFile = file;
      setBackImage(backFile);
      // Don't process yet - wait for user to click "Process Both Sides" button
      // This ensures both images are visible in preview
    }
    
    // Reset input after processing
    setTimeout(() => {
      e.target.value = '';
    }, 100);
  };

  const handleCompleteCapture = () => {
    if (frontImage) {
      if (isDoubleSided && !backImage) {
        alert('Please capture or upload the back side of the card');
        return;
      }
      onCapture(frontImage, backImage);
      setFrontImage(null);
      setBackImage(null);
      setCurrentSide('front');
    }
  };

  return (
    <div className="mt-6 space-y-6">
      {/* Double-Sided Toggle */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-800">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={isDoubleSided}
            onChange={(e) => {
              setIsDoubleSided(e.target.checked);
              if (!e.target.checked) {
                setBackImage(null);
                setCurrentSide('front');
              }
            }}
            className="w-5 h-5 rounded border-zinc-300 dark:border-zinc-700 bg-zinc-100 dark:bg-zinc-800 text-blue-600 focus:ring-2 focus:ring-blue-500"
          />
          <span className="text-sm text-zinc-700 dark:text-zinc-300">
            📄 Double-sided business card
          </span>
        </label>
      </div>

      {/* Side Indicator */}
      {isDoubleSided && (
        <div className="bg-zinc-100 dark:bg-zinc-800 rounded-xl p-3 text-center">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {currentSide === 'front' ? '📄 Front Side' : '📄 Back Side'}
          </p>
        </div>
      )}

      {/* Camera View */}
      {showCamera ? (
        <div className="relative bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-auto"
            style={{ maxHeight: '60vh' }}
          />
          <canvas ref={canvasRef} className="hidden" />
          
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-zinc-900/90 to-transparent">
            <div className="flex gap-4 justify-center">
              <button
                onClick={stopCamera}
                className="px-6 py-3 bg-zinc-100 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 rounded-full font-medium"
              >
                Cancel
              </button>
              <button
                onClick={capturePhoto}
                className="w-16 h-16 bg-white rounded-full border-4 border-zinc-300 shadow-lg active:scale-95 transition-transform"
                disabled={isProcessing}
              >
                <span className="sr-only">Capture</span>
              </button>
            </div>
            {isDoubleSided && currentSide === 'back' && frontImage && (
              <p className="text-center text-zinc-700 dark:text-zinc-300 text-sm mt-2">
                Front captured <Icon emoji="✓" size="text-sm" /> - Now capture the back
              </p>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Upload Area - Only show if front image not uploaded yet, or if not double-sided */}
          {(!frontImage || (!isDoubleSided && !frontImage)) && (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="relative border-2 border-dashed border-zinc-300 dark:border-zinc-700 rounded-2xl p-12 text-center cursor-pointer hover:border-zinc-400 dark:hover:border-zinc-600 transition-colors bg-zinc-50 dark:bg-zinc-900/50"
            >
              <div className="space-y-4">
                <Icon emoji="📷" size="text-6xl" />
                <div>
                  <p className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                    Tap to Upload
                  </p>
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleFileSelect(e, 'front')}
                className="hidden"
                disabled={isProcessing}
              />
            </div>
          )}
          
          {/* Back file input - separate, only accepts single file */}
          <input
            ref={fileInputBackRef}
            type="file"
            accept="image/*"
            multiple={false}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                console.log('Back file selected directly:', file.name);
                // Directly set back image - don't touch front image
                setBackImage(file);
              } else {
                console.log('No file selected for back side');
              }
              // Reset input after a short delay to allow processing
              setTimeout(() => {
                if (fileInputBackRef.current) {
                  fileInputBackRef.current.value = '';
                }
              }, 100);
            }}
            className="hidden"
            disabled={isProcessing}
            id="back-file-input"
          />

        </>
      )}

      {/* Preview - Hide when processing to avoid duplicate images */}
      {frontImage && !showCamera && !isProcessing && (
        <div className="space-y-3">
          <div className="relative bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
            <p className="text-xs text-zinc-600 dark:text-zinc-400 p-2 bg-zinc-100 dark:bg-zinc-800">Front Side</p>
            <img
              src={URL.createObjectURL(frontImage)}
              alt="Front of business card"
              className="w-full h-auto"
            />
          </div>
          {backImage && (
            <div className="relative bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
              <p className="text-xs text-zinc-600 dark:text-zinc-400 p-2 bg-zinc-100 dark:bg-zinc-800">Back Side</p>
              <img
                src={URL.createObjectURL(backImage)}
                alt="Back of business card"
                className="w-full h-auto"
              />
            </div>
          )}
          {isDoubleSided && !backImage && frontImage && (
            <div className="bg-zinc-100 dark:bg-zinc-800 rounded-2xl p-4 text-center">
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">Front side saved <Icon emoji="✓" size="text-sm" /></p>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-3">Now upload the back side (one image only):</p>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Upload back side clicked, ref:', fileInputBackRef.current);
                  if (fileInputBackRef.current) {
                    // Ensure the input is reset and ready for a single file
                    fileInputBackRef.current.value = '';
                    fileInputBackRef.current.click();
                  } else {
                    console.error('Back file input ref is null');
                  }
                }}
                className="px-4 py-2 bg-zinc-200 dark:bg-zinc-700 hover:bg-zinc-300 dark:hover:bg-zinc-600 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors"
              >
                <Icon emoji="📷" size="text-base" className="mr-1" /> Upload Back Side (One Image)
              </button>
            </div>
          )}
          {isDoubleSided && frontImage && backImage && (
            <button
              type="button"
              onClick={handleCompleteCapture}
              className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm transition-colors"
            >
              <Icon emoji="✓" size="text-base" className="mr-1" /> Process Both Sides
            </button>
          )}
        </div>
      )}

      {/* Processing State - Show image if available */}
      {isProcessing && (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
          {capturedImage && (
            <div className="relative">
              <img
                src={URL.createObjectURL(capturedImage)}
                alt="Business card being processed"
                className="w-full h-auto opacity-90"
              />
              <div className="absolute inset-0 bg-white/80 dark:bg-zinc-900/50 flex items-center justify-center">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-100 mb-4"></div>
                  <p className="text-zinc-700 dark:text-zinc-300 font-medium">Analyzing with AI...</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-2">This may take a few seconds</p>
                </div>
              </div>
            </div>
          )}
          {!capturedImage && (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-100 mb-4"></div>
              <p className="text-zinc-600 dark:text-zinc-400">Analyzing with AI...</p>
              <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-2">This may take a few seconds</p>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-red-700 dark:text-red-300">
          <p className="font-semibold">Error</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}
    </div>
  );
}

export default CameraCapture;

